import * as Sentry from '@sentry/react';
import {
  resetVerifiedTerminalObservations,
  armVerifiedTerminalObservations,
  verifiedTerminalBeforeSendLog,
} from './verified-terminal-observations';
export {
  observeVerifiedTerminal,
  type VerifiedTerminalObservation,
} from './verified-terminal-observations';
import { redactLuhnPansDeep } from '@heyroot/sdk-core/pan-redaction';
import { Component, createElement, isValidElement } from 'react';
import type { ErrorInfo, ReactElement, ReactNode } from 'react';
import { tracesSampler as defaultTracesSampler } from './traces-sampler';
import type { TraceSamplingContext } from './traces-sampler';

export { captureException } from '@sentry/react';
export { tracesSampler } from './traces-sampler';

const REDACT_HEADER = /(authorization|cookie|x-csrf|x-tenant)/i;

/**
 * beforeSend hook: drop request bodies (may contain PII / money / tokens) and
 * redact sensitive headers. Conservative on purpose — these are tenant/POS/
 * operator apps. Returning the (mutated) event keeps the error; returning null
 * would drop it.
 */
export function makeBeforeSend() {
  return (event: Sentry.ErrorEvent): Sentry.ErrorEvent | null => {
    // Drop EXPECTED client-side errors (4xx ApiError): validation (422),
    // not-found (404), conflict (409), forbidden (403), etc. are user-input /
    // permission outcomes that surface inline in the UI, not app bugs. Keep 5xx
    // (real server faults) AND 401 (unauthenticated — may signal a real auth
    // bug, so it is never dropped even if a future message embeds "401").
    const ex = event.exception?.values?.[0];
    if (
      ex?.type === 'ApiError' &&
      typeof ex.value === 'string' &&
      /\b4\d\d\b/.test(ex.value) &&
      !/\b401\b/.test(ex.value)
    ) {
      return null;
    }
    // Drop expected Firebase auth-token-expiry (STAGING-Z): a stored session
    // token simply expired — normal, not a bug — and the user is routed back
    // to sign in. Never a real fault, so it should never page anyone.
    if (
      ex?.type === 'FirebaseError' &&
      typeof ex.value === 'string' &&
      /auth\/user-token-expired/.test(ex.value)
    ) {
      return null;
    }
    if (event.request) {
      // Request body never gets sent — it is the highest-PII surface.
      delete event.request.data;
      const headers = event.request.headers;
      if (headers) {
        for (const key of Object.keys(headers)) {
          if (REDACT_HEADER.test(key)) delete headers[key];
        }
      }
    }
    // #15086: scrub Luhn-valid PANs from every string of the event.
    return redactLuhnPansDeep(event);
  };
}

export interface InitSentryWebOptions {
  dsn: string;
  environment: string;
  release: string;
  app: 'backoffice' | 'admin';
  verifiedTerminalObservations?: boolean;
  /** Flat sample rate. When set it replaces the default `tracesSampler` (tests, local overrides). */
  tracesSampleRate?: number;
  /** Custom sampler. Ignored when `tracesSampleRate` is set. Defaults to the shared `tracesSampler`. */
  tracesSampler?: (ctx: TraceSamplingContext) => number;
}

/**
 * Initialize Sentry for a Vite/React SPA. No-op when dsn is empty (local dev,
 * or an env without Sentry configured) so the app still boots.
 */
export function initSentryWeb(opts: InitSentryWebOptions): void {
  resetVerifiedTerminalObservations();
  if (!opts.dsn) return;
  const observations = opts.app === 'backoffice' && opts.verifiedTerminalObservations === true;
  Sentry.init({
    ...(observations ? { enableLogs: true, beforeSendLog: verifiedTerminalBeforeSendLog } : {}),
    dsn: opts.dsn,
    environment: opts.environment,
    release: opts.release,
    sendDefaultPii: false,
    // A flat rate (when given) wins; otherwise sample by route (drop probes/static, keep payments).
    // Sentry honours tracesSampler over tracesSampleRate, so only ever pass one.
    ...(opts.tracesSampleRate !== undefined
      ? { tracesSampleRate: opts.tracesSampleRate }
      : { tracesSampler: opts.tracesSampler ?? defaultTracesSampler }),
    integrations: [Sentry.browserTracingIntegration()],
    ignoreErrors: [
      // Transient network blip during Firebase auth / token refresh — retried
      // by the SDK, not an app bug. (The fire-and-forget refresh in the
      // backoffice api-client also swallows it now.)
      /auth\/network-request-failed/,
      // Belt-and-suspenders for the ApiError 4xx message shapes, in case
      // exception.type isn't resolved to 'ApiError' in the captured event.
      // (All ApiError classes set name='ApiError', so beforeSend's type check
      // is the primary path; these regexes are the backstop.)
      /ApiError: HTTP 4\d\d/,
      /ApiError: 4\d\d\b/,
    ],
    beforeSend: makeBeforeSend(),
    beforeBreadcrumb: (breadcrumb) => redactLuhnPansDeep(breadcrumb),
    beforeSendTransaction: (event) => redactLuhnPansDeep(event),
    initialScope: { tags: { app: opts.app } },
  });
  if (observations) armVerifiedTerminalObservations();
}

// String(value) throws for an adversarial toString()/Symbol.toPrimitive, and
// an unbounded result could carry an arbitrary amount of PII/secrets into a
// Sentry event (the ONE thing makeBeforeSend above exists to keep off the
// wire, via a path it doesn't scrub: see stringifyForReport's own callers).
// Exported so apps/backoffice/src/components/shell/error-boundary.tsx's
// RouteErrorBoundary -- an independent ErrorBoundary for a different render
// tree, fixed for the same non-Error-normalization bug -- can share the one
// safe conversion instead of each carrying its own copy.
export function stringifyForReport(value: unknown, maxLength = 200): string {
  try {
    return String(value).slice(0, maxLength);
  } catch {
    return '[unstringifiable]';
  }
}

// Mirrors @sentry/react's error.js setCause: link a synthetic
// "React ErrorBoundary <Name>" error (stack = componentStack) onto `error`
// via `cause`, walking to the end of any existing cause chain (cycle-guarded)
// so this is additive rather than clobbering a cause the throw site already
// set. Gives Sentry's stack-trace view the component tree AND keeps
// LinkedErrors grouping consistent with events captured by the real
// @sentry/react ErrorBoundary elsewhere in the org -- without it, a real
// Error reported through THIS boundary groups differently than the same
// error reported through the vendor's own boundary.
function linkComponentStack(error: Error, componentStack: string): void {
  const seen = new WeakSet<Error>();
  let current = error;
  while (current.cause instanceof Error && !seen.has(current)) {
    seen.add(current);
    current = current.cause;
  }
  const boundaryError = new Error(current.message);
  boundaryError.name = `React ErrorBoundary ${current.name}`;
  boundaryError.stack = componentStack;
  current.cause = boundaryError;
}

// Normalizes a value caught by an ErrorBoundary and reports it to Sentry.
// @sentry/react's OWN ErrorBoundary (build/cjs/errorboundary.js
// componentDidCatch), for the specific case of a non-Error throw
// (undefined/null/a plain object), forwards the RAW thrown value straight
// into Sentry.captureException with no normalization: error.js's
// captureReactException only builds a componentStack-linked synthetic error
// when `core.isError(error)` is true, so a non-Error throw skips that path
// entirely and reports as a near-useless event: type "Error", value
// literally "undefined" (HEYROOT-STAGING-D). It exposes no hook that can
// substitute the captured exception — beforeCapture only mutates the scope,
// and onError fires AFTER the bad capture already happened. Exported (not
// just used internally) so the exact normalization this fixes is
// unit-testable without exercising a full React render.
//
// Also replicates the three pieces of Sentry-event fidelity the real
// ErrorBoundary attaches on EVERY capture (Error or not) that a naive
// Sentry.captureException(error) call would otherwise lose: the React
// `componentStack` as scope context (error.js's captureReactException calls
// `scope.setContext('react', { componentStack })`), the componentStack-linked
// synthetic error chained via `cause` (see linkComponentStack above -- run
// for both a real Error AND a normalized non-Error throw, since the latter
// is exactly the case that most needs the attribution), and an exception
// `mechanism.handled` tag
// (errorboundary.js defaults this to `!!props.fallback`) that drives
// Sentry's handled/unhandled classification and crash-free-rate metric.
export function reportBoundaryError(
  error: unknown,
  componentStack?: string,
  handled = true,
): { reported: Error; eventId: string } {
  const isRealError = error instanceof Error;
  const reported = isRealError
    ? error
    : new Error(`ErrorBoundary caught a non-Error value: ${stringifyForReport(error)}`);
  const captureContext: {
    contexts?: { react: { componentStack: string } };
    extra?: { raw: string; rawType: string };
    fingerprint?: string[];
  } = {};
  if (componentStack !== undefined) {
    captureContext.contexts = { react: { componentStack } };
    // Chained for a non-Error throw too (HEYROOT-STAGING-1P): without this,
    // a non-Error report gives Sentry's stack-trace view nothing but the
    // synthetic message -- no attribution to which component threw. The
    // resulting cause is React's component stack, not the original
    // JavaScript throw site; it narrows the search, it doesn't pinpoint it.
    linkComponentStack(reported, componentStack);
  }
  if (!isRealError) {
    captureContext.extra = { raw: stringifyForReport(error), rawType: typeof error };
    // Stable grouping: the synthetic Error is built here, so its stack (and the linked
    // componentStack cause) differ per bundle hash and render path, splitting one real problem
    // ("caught a non-Error value: undefined") into many issues. Group by what was thrown instead.
    // The componentStack cause stays on each event for attribution.
    captureContext.fingerprint = [
      'react-error-boundary-non-error',
      typeof error,
      stringifyForReport(error),
    ];
  }
  const eventId = Sentry.captureException(reported, { captureContext, mechanism: { handled } });
  return { reported, eventId };
}

export type ErrorBoundaryFallbackRender = (errorData: {
  error: unknown;
  componentStack: string;
  eventId: string;
  resetError: () => void;
}) => ReactElement;

export interface ErrorBoundaryProps {
  children?: ReactNode;
  fallback?: ReactElement | ErrorBoundaryFallbackRender;
  /** Called after Sentry reporting, with the ORIGINAL (unnormalized) thrown value. */
  onError?: ((error: unknown, componentStack: string, eventId: string) => void) | undefined;
}

type ErrorBoundaryState =
  | { componentStack: null; error: null; eventId: null }
  | { componentStack: string; error: unknown; eventId: string };

const INITIAL_ERROR_BOUNDARY_STATE: ErrorBoundaryState = {
  componentStack: null,
  error: null,
  eventId: null,
};

/**
 * Alternative to @sentry/react's own ErrorBoundary, covering the subset of
 * its public shape this repo's call sites actually use
 * (children/fallback/onError) — NOT a full drop-in: the real ErrorBoundary
 * also exposes resetKeys/onReset/onMount/onUnmount/showDialog/dialogOptions/
 * beforeCapture/a `handled` prop and function-form `children`, none of
 * which this class implements. Routes the caught value through
 * reportBoundaryError above (component-stack context + a `mechanism.handled`
 * tag mirroring @sentry/react's own default, PLUS the non-Error
 * normalization @sentry/react's real ErrorBoundary does not do) instead of
 * reporting it raw. Mirrors the identical non-Error-normalization fix
 * already applied to RouteErrorBoundary
 * (apps/backoffice/src/components/shell/error-boundary.tsx).
 */
export class ErrorBoundary extends Component<ErrorBoundaryProps, ErrorBoundaryState> {
  override state: ErrorBoundaryState = INITIAL_ERROR_BOUNDARY_STATE;

  override componentDidCatch(error: unknown, errorInfo: ErrorInfo): void {
    const componentStack = errorInfo.componentStack ?? '';
    // `!!this.props.fallback` mirrors @sentry/react's own
    // `handled = props.handled != null ? props.handled : !!props.fallback`
    // (errorboundary.js) -- an ErrorBoundary that renders a fallback UI IS
    // the "handled" case for Sentry's grouping/classification.
    const { reported, eventId } = reportBoundaryError(error, componentStack, !!this.props.fallback);
    // onError keeps the ORIGINAL raw value (see its own doc comment above) --
    // callers that want the exact thrown value (e.g. chunk-reload recovery
    // matching on a message substring) get it unfiltered. State/the fallback
    // render prop get the NORMALIZED `reported` error instead of the raw
    // value: Sentry already sees the normalized error, and a fallback that
    // reasonably assumes `errorData.error` is a real Error (this whole file
    // exists to make that assumption safe) would otherwise crash on a
    // non-Error throw the normalization was supposed to fix.
    this.props.onError?.(error, componentStack, eventId);
    this.setState({ error: reported, componentStack, eventId });
  }

  private resetErrorBoundary = (): void => {
    this.setState(INITIAL_ERROR_BOUNDARY_STATE);
  };

  override render(): ReactNode {
    const { fallback, children } = this.props;
    const { componentStack, error, eventId } = this.state;
    if (componentStack === null) return children;
    const element =
      typeof fallback === 'function'
        ? createElement(fallback, {
            error,
            componentStack,
            eventId,
            resetError: this.resetErrorBoundary,
          })
        : fallback;
    return isValidElement(element) ? element : null;
  }
}
